import { PlusIcon } from 'lucide-react';
import { cn } from '@rocco/utils/cn';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@rocco/components/tooltip';
import { getUserOccupation, useUserLoggedIn } from '~/modules/user';
import type { Conversation, PartialConversation } from '../../../types';
import { ParticipantAvatar } from '../../avatar/ParticipantAvatar';

/**
 * The "Members" tab content of the chat box conversation settings.
 * @see {@link ChatBoxSettings}
 */
export function ChatBoxSettingsMembers({
    className,
    conversation,
}: {
    className?: string;
    conversation: Conversation | PartialConversation;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();

    return (
        <div className={cn('flex flex-col gap-[8px]', className)}>
            <TooltipProvider delayDuration={0}>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <button className="flex w-full items-center gap-[8px] rounded-full p-[8px] hover:bg-white/50">
                            <PlusIcon
                                className="size-[32px] shrink-0 rounded-full border border-black p-[6px]"
                                strokeWidth="1px"
                            />
                            <span className="min-w-0 flex-1 break-words text-left text-[14px] font-medium leading-tight">
                                Add people to this conversation
                            </span>
                        </button>
                    </TooltipTrigger>
                    <TooltipContent>Coming soon™</TooltipContent>
                </Tooltip>
            </TooltipProvider>
            <ul className="flex min-h-0 flex-1 flex-col overflow-y-auto">
                {conversation.Participants.map(({ ID, Identity }) => (
                    <li
                        key={ID}
                        className="group flex items-center gap-[8px] rounded-full p-[8px] hover:bg-white/50"
                    >
                        <ParticipantAvatar
                            className="size-[36px]"
                            fallbackClassName="!text-[14px]"
                            participant={Identity}
                        />
                        <div className="min-w-0 flex-1 space-y-[2px]">
                            <div className="truncate text-[14px] font-medium leading-tight">
                                {Identity.__typename === 'Professional'
                                    ? Identity.Title
                                    : Identity.Name}
                                {isLoggedIn &&
                                    Identity.__typename === 'Member' &&
                                    Identity.ID === user.ID &&
                                    ' (you)'}
                            </div>
                            <div className="truncate text-[12px] leading-tight opacity-70">
                                {Identity.__typename === 'Professional'
                                    ? Identity.PrimaryCategory?.PrettyTitle
                                    : getUserOccupation(Identity)}
                            </div>
                        </div>
                        <TooltipProvider delayDuration={0}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <button className="invisible rounded-[8px] border border-black/70 px-[8px] py-[4px] font-medium text-black/70 hover:bg-black/10 group-hover:visible">
                                        Remove
                                    </button>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                    Coming soon™
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    </li>
                ))}
            </ul>
        </div>
    );
}
